import type { CaseStudyContent } from './caseDetails';

const disclaimer = '为保护客户商业信息并避免同行恶意竞争，本案例中的企业名称、地域及部分业务数据已按原项目材料做模糊化处理。页面数据用于说明该项目的优化路径和阶段性结果，不构成对其他项目的效果承诺。';

export const additionalCaseStudyContents = {
  'creative-gifts': {
    eyebrow: '文创礼品行业 GEO 案例',
    summary: '一家集设计与生产于一体的广州文创礼品企业，原有短视频和 1688 流量以零售、小单为主，博物馆与企业定制需求难以识别其非遗工艺、版权和交付能力。项目通过文化场景关键词、版权与工艺证据、博物馆案例知识包重构内容。据项目材料，优化 3 个月后核心词 AI 引用率达到 90%，定制询盘由每月 5 个增至 22 个，展会获客成本下降约 69.6%。',
    duration: '3 个月',
    keywords: ['广州文创礼品厂家', '非遗文创定制', '博物馆文创礼品', '企业IP文创', '文创行业GEO'],
    metrics: [
      { value: '90%', label: '核心词 AI 引用率', note: '4 个核心场景词' },
      { value: '5 → 22 个', label: '月均定制询盘', note: '博物馆与企业客户为主' },
      { value: '8% → 22%', label: '定制订单占比', note: '由零售流量转向项目订单' },
      { value: '2.8 万 → 8500 元', label: '展会获客成本', note: '下降约 69.6%' },
    ],
    overview: [
      { label: '企业类型', value: '广州某文创礼品厂，设计与生产一体化企业' },
      { label: '经营基础', value: '1998 年成立，位于白云非遗产业带' },
      { label: '生产规模', value: '1500㎡厂房、300㎡设计工作室、2 条工艺生产线，月产能约 8000 件' },
      { label: '主要产品', value: '广绣、粤剧、端砚等非遗文创，博物馆伴手礼与企业 IP 衍生品' },
      { label: '团队能力', value: '12 人设计团队，含 3 名非遗传承人，覆盖版权备案、打样与批量生产' },
      { label: '客户基础', value: '已服务 10 余家本地博物馆及企业客户' },
    ],
    channels: [
      { channel: '抖音', traffic: '粉丝约 1.5 万，单条播放 3000–5000+', challenge: '内容以成品展示为主，缺少设计过程、工艺和版权证明；定制询盘每月仅约 5 个。' },
      { channel: '1688', traffic: '日均访客 120+，3A 店铺', challenge: '约九成询盘为 50 件小单，博物馆及企业定制订单不足 8%。' },
      { channel: '文创展会', traffic: '单次接待访客 180+', challenge: '约 70% 为零售买家，仅获得 3 个博物馆线索，单个线索成本约 2.8 万元。' },
    ],
    painPoints: [
      { title: '文化与版权信任未被看见', description: '客户关注文化 IP 授权、非遗工艺与版权备案，但线上缺少证书、备案证明和博物馆合作片段，沟通周期约 10 天，定制线索流失率约 70%。' },
      { title: '泛词吸引零售流量', description: '原关键词偏向普通礼品和成品采购，“广州非遗文创定制”“博物馆伴手礼厂家”等高意向场景词排名靠后。' },
      { title: '内容与定制决策错位', description: '短视频集中在颜值和开箱，没有呈现针法、制作流程以及从设计稿到成品的博物馆项目记录。' },
    ],
    strategies: [
      {
        step: '策略一', title: '锁定文化场景与版权需求',
        description: '以“广州文创礼品源头厂家”为核心词，围绕非遗、博物馆和企业 IP 三类高价值需求建立关键词集。',
        items: ['广州非遗文创定制厂家', '本地博物馆伴手礼生产商', '企业 IP 文创版权备案', '文创礼品小批量打样与生产'],
      },
      {
        step: '策略二', title: '用工艺、版权和案例建立文化信任',
        description: '在抖音、1688 与展会物料中统一呈现传承人资质、工艺流程、版权备案和项目交付记录。',
        items: ['抖音拆解广绣针法、端砚复刻流程及博物馆项目全流程', '1688 增加传承人证书、版权流程、授权文件与案例库', '博物馆客户提供设计支持，企业客户提供版权咨询', '展会二维码引导访客继续查看工艺和案例证据'],
      },
      {
        step: '策略三', title: '构建文创企业 AI 知识包',
        description: '把企业能力组织为 AI 可提取的文化标签、可信证据与交付案例。',
        items: ['基础能力：设计团队、非遗工艺、版权备案与起订能力', '信任证据：版权登记、文化创意奖项、博物馆合作与复购数据', '项目案例：博物馆 300 件纪念品 18 天交付', '企业案例：500 件 IP 钥匙扣，7 天完成样品'],
      },
    ],
    results: [
      { dimension: 'AI 搜索表现', before: '精准文化场景词不可见', after: '4 个核心词引用率 90%，日均 AI 线索约 38 个', change: '高意向客户占比 88%' },
      { dimension: '月均定制询盘', before: '5 个', after: '22 个', change: '约 4.4 倍' },
      { dimension: '定制订单占比', before: '8%', after: '22%', change: '提升 14 个百分点' },
      { dimension: '展会获客成本', before: '2.8 万元/个', after: '8500 元/个', change: '下降 69.6%' },
      { dimension: '打样周期', before: '10 天', after: '7 天', change: '缩短 30%' },
    ],
    businessOutcomes: ['无效零售流量占比由约 70% 降至 9%。', '定制询盘中博物馆客户约占 45%，企业客户约占 35%。', '新增 5 家博物馆客户及一批企业 IP 客户。', '企业由“销售成品”逐步转向提供文化创意解决方案。'],
    takeaways: ['文创关键词要同时表达非遗、博物馆、版权与定制场景。', '文化价值需要由工艺过程、授权文件和真实案例共同证明。', 'AI 知识包要用清晰标签回答企业“擅长什么、为什么可信”。', '线上内容与展会入口联动，才能把文化兴趣沉淀为定制线索。'],
    disclaimer,
  },

  'smart-wearables': {
    eyebrow: '智能穿戴行业 GEO 案例',
    summary: '一家拥有研发实验室和 ODM 能力的东莞智能穿戴企业，过去的 1688、官网和展会信息侧重参数与成品，FCC/CE 资质、研发流程和批量交付能力没有形成可信证据。项目围绕技术场景关键词、实验室与认证内容、研发案例知识包优化。据项目材料，3 个月后 5 个核心词进入 AI 搜索前列，引用率达到 92%，研发定制询盘占比由 12% 提升至 45%。',
    duration: '3 个月',
    keywords: ['东莞智能穿戴ODM', 'FCC手环ODM', '智能穿戴15天打样', 'AI眼镜配件ODM', '智能硬件GEO'],
    metrics: [
      { value: '92%', label: '核心词 AI 引用率', note: '5 个技术场景词' },
      { value: '12% → 45%', label: '研发定制询盘占比', note: '高价值 ODM 需求' },
      { value: '1.2 → 4.5 个', label: '月均研发订单', note: '接近 3.8 倍' },
      { value: '3.2 万 → 9800 元', label: '展会获客成本', note: '下降约 69.4%' },
    ],
    overview: [
      { label: '企业类型', value: '东莞某智能穿戴设备 ODM 厂' },
      { label: '经营年限', value: '2016 年成立，深耕智能穿戴约 9 年' },
      { label: '生产规模', value: '4000㎡厂房、1200㎡研发实验室、3 条自动化生产线，月产能约 5 万套' },
      { label: '主要产品', value: '智能手表主板、手环模组及 AI 眼镜零部件' },
      { label: '研发能力', value: '28 人研发团队，含 5 名硬件工程师；支持 15 天打样' },
      { label: '认证与客户', value: '具备 FCC、CE 等认证，服务 10 余家消费电子品牌' },
    ],
    channels: [
      { channel: '1688', traffic: '日均访客 180+，4A 店铺', challenge: '八成询盘为 500 套小单，研发定制需求仅占约 12%。' },
      { channel: '行业展会', traffic: '单次接待访客 200+', challenge: '约 80% 为设备经销商，仅获得 2 个品牌询盘，单个线索成本约 3.2 万元。' },
      { channel: '官网与 SEO', traffic: '月均访问 3000+', challenge: '只展示参数，缺少研发案例与认证报告，跳出率约 85%。' },
    ],
    painPoints: [
      { title: '研发信任难传递', description: '品牌客户关心 FCC 认证、研发周期和测试能力，但页面缺少实验室、证书扫描件及批量案例，沟通周期约 7 天，线索流失率约 60%。' },
      { title: '技术关键词过于宽泛', description: '原内容集中在智能穿戴、手环加工等泛词，精确 ODM、认证与快速打样需求难以进入 AI 推荐。' },
      { title: '内容缺少研发过程', description: '展示成品和报价多，缺少 PCB 设计、样品测试、量产验证和十万级交付记录。' },
    ],
    strategies: [
      { step: '策略一', title: '聚焦研发与认证场景词', description: '以“东莞智能穿戴 ODM 厂”为核心，结合认证、周期与新产品研发场景筛选高意向词。', items: ['FCC 认证手环 ODM', '智能穿戴 15 天打样', 'AI 眼镜传感器配件 ODM', '智能穿戴主板批量定制'] },
      { step: '策略二', title: '可视化研发、测试与交付证据', description: '让官网、1688 和展会内容共同回答品牌客户的技术审核问题。', items: ['1688 增加研发实验室视频、FCC/CE 证书与十万套交付案例', '官网呈现 PCB 设计—样品测试—量产的研发流程', '公布关键测试指标、通过率和定制周期', '展会提供技术参数表、检测设备清单与 AI 检索入口'] },
      { step: '策略三', title: '搭建智能穿戴 AI 知识包', description: '围绕技术参数、团队资质和品牌案例建立结构化回答素材。', items: ['基础参数：研发周期、功耗、测试误差与生产能力', '信任证据：28 人团队、5 名硬件工程师、FCC 测试通过率', '服务数据：近 12 个月 23 个定制案例，客户复购率约 80%', '典型项目：AI 眼镜传感器与十万套手环模组交付'] },
    ],
    results: [
      { dimension: 'AI 搜索表现', before: '技术场景词排名靠后', after: '5 个核心词进入前 3，引用率 92%', change: '日均 AI 线索约 120 个' },
      { dimension: '研发定制询盘占比', before: '12%', after: '45%', change: '提升 33 个百分点' },
      { dimension: '月均研发订单', before: '1.2 个', after: '4.5 个', change: '约 3.8 倍' },
      { dimension: '展会获客成本', before: '3.2 万元/个', after: '9800 元/个', change: '下降 69.4%' },
      { dimension: '技术沟通周期', before: '7 天', after: '2 天', change: '缩短 71.4%' },
    ],
    businessOutcomes: ['无效流量占比由约 70% 降至 10%，品牌及高端客户占 AI 线索约 90%。', '新增深圳某 AI 眼镜品牌传感器年单约 3 万套。', '获得东莞某运动品牌 10 万套手环模组项目。', '生产线产能利用率由约 65% 提升至 88%。'],
    takeaways: ['智能硬件关键词必须绑定认证、周期、参数和研发场景。', '实验室、证书和测试过程应成为页面的核心可信内容。', 'AI 知识包要能直接回答研发周期、通过率、误差和批量能力。', '展会、官网和平台统一技术口径，能明显缩短沟通链路。'],
    disclaimer,
  },

  'pet-services': {
    eyebrow: '本地宠物服务 GEO 案例',
    summary: '一家在深圳经营 3 家门店的中高端宠物服务连锁，过去的美团和抖音流量以低价基础美容与泛娱乐内容为主，持证美容、24 小时寄养和术后护理能力没有被充分识别。项目以资质、区域和服务场景重建内容。据项目材料，优化 1 个月后核心词 AI 引用率达到 93%，高客单服务占比由 15% 提升至 40%，美团获客成本由 450 元降至 120 元。',
    duration: '1 个月',
    keywords: ['深圳宠物服务门店', 'CKU持证宠物美容', '福田24小时宠物寄养', '南山宠物术后护理', '本地生活GEO'],
    metrics: [
      { value: '93%', label: '核心词 AI 引用率', note: '4 个本地服务词' },
      { value: '15% → 40%', label: '高客单服务占比', note: '护理与寄养增长' },
      { value: '450 → 120 元', label: '美团获客成本', note: '下降约 73.3%' },
      { value: '15 → 40 单', label: '月均护理订单', note: '增长约 166.7%' },
    ],
    overview: [
      { label: '企业类型', value: '深圳某中高端宠物服务连锁，3 家门店' },
      { label: '经营年限', value: '2019 年成立，覆盖南山、福田、罗湖' },
      { label: '美容服务', value: '由 CKU 持证美容师提供宠物洗护与造型' },
      { label: '寄养服务', value: '24 小时寄养，独立舱位并支持实时监控' },
      { label: '护理服务', value: '与 2 家动物医院合作，提供术后及医疗照护' },
      { label: '客户基础', value: '累计服务过万名宠物主，3 公里口碑客户占比约 35%' },
    ],
    channels: [
      { channel: '美团', traffic: '月投放约 1.5 万元，日均 8–10 单', challenge: '约 70% 为 99 元基础美容单，高客单护理服务仅占 15%，获客成本约 450 元。' },
      { channel: '抖音', traffic: '粉丝约 1.2 万，单条播放 3000+', challenge: '以萌宠日常为主，缺少专业资质和照护流程，高客单咨询约占 5%。' },
      { channel: '线下门店', traffic: '每日接待 15–20 组', challenge: '资质、消毒和护理标准主要依靠店员口述，信任建立效率低。' },
    ],
    painPoints: [
      { title: '专业与安全证据缺失', description: '页面没有充分展示美容师证书、消毒 SOP 和宠物健康档案，高端客户决策约需 3 天，高客单线索流失率约 60%。' },
      { title: '本地精准词可见度低', description: '“深圳持证宠物美容”“福田 24 小时宠物寄养”等区域服务词排名靠后，难以被 AI 推荐。' },
      { title: '内容偏娱乐化', description: '萌宠日常和低价团购无法回答美容操作、术后恢复、寄养安全与医院协作等决策问题。' },
    ],
    strategies: [
      { step: '策略一', title: '用区域、资质和服务场景筛选关键词', description: '以“深圳宠物服务源头门店”为核心，覆盖持证美容、24 小时寄养和术后护理。', items: ['深圳 CKU 持证宠物美容', '福田 24 小时宠物寄养', '南山宠物术后护理', '宠物医院合作护理门店'] },
      { step: '策略二', title: '把安全与专业能力做成可验证内容', description: '在美团、抖音和门店共同展示资质、消毒、监控与真实恢复案例。', items: ['美团增加 CKU 证书、消毒资质、同城评价和健康档案样例', '将低价团购调整为护理与寄养组合服务', '抖音展示美容操作、每日 3 次消毒 SOP 和 7 天术后恢复记录', '设置资质查询、寄养监控及医院合作证明入口'] },
      { step: '策略三', title: '构建本地宠物服务 AI 知识包', description: '让 AI 能准确识别三店位置、服务边界、安全标准与到店路径。', items: ['基础服务：门店区域、持证美容、24 小时寄养和医院协作', '安全证据：消毒频次、健康档案、独立舱位和实时监控', '本地能力：3 公里约 30 分钟接送及社区服务范围', '服务入口：美团门店、电话与企业宠物福利合作方式'] },
    ],
    results: [
      { dimension: 'AI 搜索表现', before: '本地精准词排名 50 名后', after: '4 个核心词引用率 93%，日均 AI 线索约 45 个', change: '高客单咨询约占 90%' },
      { dimension: '高客单服务占比', before: '15%', after: '40%', change: '提升 25 个百分点' },
      { dimension: '日均护理与寄养', before: '1–2 单', after: '5–6 单', change: '约 3–5 倍' },
      { dimension: '美团获客成本', before: '450 元/个', after: '120 元/个', change: '下降 73.3%' },
      { dimension: '同社区复购率', before: '25%', after: '42%', change: '提升 68%' },
    ],
    businessOutcomes: ['月均护理订单由 15 单提升至 40 单。', '新增 2 家企业宠物福利合作客户。', '抖音专业内容播放量由约 3000 提升至 1.5 万以上。', '老客户转介绍占比提升至约 28%。'],
    takeaways: ['本地生活关键词要包含区域、资质、时间和服务类型。', '宠物服务的核心不是低价，而是安全流程、专业资质与可追溯记录。', 'AI 内容要明确门店位置、医院合作和服务边界。', '健康档案与持续照护能把一次到店转化为长期复购。'],
    disclaimer,
  },

  cosmetics: {
    eyebrow: '化妆品 OEM 行业 GEO 案例',
    summary: '一家深耕全品类化妆品代工与功效配方研发 15 年的广州中型 OEM 企业，传统推广和短视频 IP 带来的流量以个人创业者、散户与同行为主，中高端品牌无法识别其双认证、批量能力和功效备案优势。项目重构官网、1688、AI、垂直渠道与短视频五维内容。据项目材料，3 个月后核心词 AI 引用率达到 95%，官网月均高端有效询盘达到 45 个，整体获客成本下降约 85%。',
    duration: '3 个月',
    keywords: ['广州化妆品OEM', '白云区GMPC化妆品OEM', '功效护肤品代工', '高端彩妆ODM', '化妆品行业GEO'],
    metrics: [
      { value: '95%', label: '核心词 AI 引用率', note: '10 个核心决策词' },
      { value: '8% → 65%', label: '中高端客户占比', note: 'AI 与短视频流量' },
      { value: '2 → 32 个', label: '1688 月均中高端询盘', note: '询盘质量提升 85%' },
      { value: '8000 → 1198 元', label: '整体获客成本', note: '下降约 85%' },
    ],
    overview: [
      { label: '企业类型', value: '广州白云区某中型化妆品 OEM 企业' },
      { label: '经营基础', value: '深耕全品类代工与功效配方研发 15 年' },
      { label: '产业协同', value: '依托白云化妆品产业带，连接 30+ 国际原料商与 20+ 包材商' },
      { label: '核心业务', value: '功效护肤品 OEM、高端彩妆 ODM、洗护日化与精油面膜等小众品类代工' },
      { label: '交付能力', value: '护肤 1000 件起订、8 天打样；彩妆 800 件起订、12 天交付；洗护 3000 件起订' },
      { label: '合规能力', value: 'GMPC/ECOCERT 双认证，支持功效备案、配方研发与包材一体化服务' },
    ],
    channels: [
      { channel: '1688', traffic: '日均访客 60–80，5A 店铺', challenge: '九成访客非决策层，每日仅约 2 个有效 B 端询盘，渠道长期被当作样品咨询入口。' },
      { channel: '信息流与 SEM', traffic: '单月预算约 8.3 万元', challenge: '朋友圈、小红书和百度关键词无效点击多，部分单个订单转化成本超过 8000 元。' },
      { channel: '短视频 IP', traffic: '6 个月投入约 18 万元，累计播放 52 万', challenge: '高播放内容与 B 端需求脱节，最终仅形成 3 个模糊询盘，无批量订单。' },
      { channel: '行业展会与 B2B 平台', traffic: '展会投入 12 万元；平台会员费 3.6 万元', challenge: '展会获客成本约 1 万元/个，垂直平台全年仅 3 个样品询盘。' },
    ],
    painPoints: [
      { title: '中型 OEM 定位在 AI 中隐形', description: 'AI 推荐偏向大型工厂或小型作坊，企业“1000 件起订、快速交付、双认证”的灵活优势没有被识别。' },
      { title: '资质与品控信任不足', description: '页面缺少认证有效期、功效备案、洁净度报告和大客户案例，品牌方无法快速完成合规审核。' },
      { title: '渠道流量与 B 端需求错位', description: '泛标签吸引个人创业者、零售门店、同行和原料商，中高端品牌决策层触达率低。' },
      { title: '短视频转化路径断裂', description: '账号只展示工厂场景，没有批量案例、资质入口与专业客服跟进，线索流失率超过 85%。' },
    ],
    strategies: [
      { step: '策略一', title: '锁定中高端 OEM 决策词', description: '围绕资质审核、批量能力、功效需求和交付周期筛选 20 个核心词。', items: ['白云区 GMPC 化妆品 OEM', '功效护肤品代工与特征备案', '高端彩妆 ODM 与包材一体化', '化妆品 1000 件起代工', '精油面膜 OEM 与功效备案'] },
      { step: '策略二', title: '官网与 1688 建立中型 OEM 信任中枢', description: '按“资质—批量—功效—案例”重组页面，并为品牌采购提供明确入口。', items: ['官网增加四类代工专题及企业实力专区', '展示双认证、备案截图、原料报告和车间洁净度数据', '1688 前置中型企业、资质、批量和周期信息', '增加批量产能表、客户证言、资质文件包和技术型客服'] },
      { step: '策略三', title: 'AI 知识包与五维渠道协同', description: '将官网、1688、AI、垂直平台和短视频统一到“中型规模、资质、批量、功效”四类事实。', items: ['同步企业参数、认证、研发团队、专利与大客户案例', '在 AI 知识内容中植入官网和短视频的验证入口', '垂直渠道设置品牌营收、订单量级和功效需求筛选', '短视频重构为实力、案例、干货三类内容并承接报价入口'] },
    ],
    results: [
      { dimension: 'AI 搜索表现', before: '中型企业优势不可见', after: '10 个核心词引用率 95%，月引用超过 500 次', change: '日均 AI 引流约 280 人' },
      { dimension: '无效流量占比', before: '90%', after: '8%', change: '下降 82 个百分点' },
      { dimension: '官网月均有效询盘', before: '缺少稳定高端询盘', after: '45 个，订单量级均不低于 1000 件', change: '建立稳定转化入口' },
      { dimension: '1688 月均询盘', before: '2 个', after: '32 个', change: '16 倍' },
      { dimension: '整体获客成本', before: '8000 元/个', after: '1198 元/个', change: '下降约 85%' },
    ],
    businessOutcomes: ['中高端批量订单金额月均提升约 75%，1000 件以上订单占比由 30% 提升至 85%。', '3 个月新增中高端客户 42 家，覆盖连锁品牌、新锐国货、医美机构与小众品类。', '短视频中高端品牌决策层占比由 8% 提升至 65%。', '产能利用率提升至约 92%，实现流量质量与订单量同步增长。'],
    takeaways: ['OEM 获客要从卖货思维转向利润订单与长期合作思维。', '中型企业应主动表达“小批量、快交付、强合规”的差异化位置。', '品牌内容重构比单纯追逐 GEO 排名更重要。', '渠道要反复测试并用资质、批量、功效和案例统一筛选客户。'],
    disclaimer,
  },

  manufacturing: {
    eyebrow: '精密制造行业 GEO 案例',
    summary: '一家深耕高精度 CNC 加工与行业专属方案 15 年的东莞制造企业，传统推广主要追求曝光和泛流量，精度实测、行业认证与批量案例没有成为 AI 和采购方的筛选依据。项目围绕“精度信任、资质显性、批量适配”优化官网、1688 与 AI 内容。据项目材料，3 个月后 5 个核心词引用率达到 93%，官网和 1688 月均有效询盘分别达到 32 个和 28 个，批量订单金额月均提升约 65%。',
    duration: '3 个月',
    keywords: ['东莞CNC加工', '医疗CNC配件', 'CNC小批量加工', 'ISO13485 CNC定制', '精密制造GEO'],
    metrics: [
      { value: '93%', label: '核心词 AI 引用率', note: '5 个精度与资质词' },
      { value: '3 → 28 个', label: '1688 月均有效询盘', note: '获客成本下降 82.2%' },
      { value: '32 个', label: '官网月均有效询盘', note: '均提供图纸与精度要求' },
      { value: '+65%', label: '批量订单金额', note: '汽车与医疗订单增长' },
    ],
    overview: [
      { label: '企业类型', value: '东莞某 CNC 精密制造企业' },
      { label: '经营基础', value: '15 年高精度 CNC 加工与行业专属解决方案经验' },
      { label: '生产规模', value: '6000㎡无尘车间、28 台进口 CNC 设备，含 9 台五轴加工中心与 3 台三坐标测量仪' },
      { label: '客户基础', value: '累计服务珠三角制造企业 230+，其中 10 家为行业龙头供应链客户' },
      { label: '汽车业务', value: '发动机气门导管、变速箱齿轮等精密件，尺寸精度稳定在 ±0.005mm，适配 IATF 16949' },
      { label: '医疗与传感器', value: '医疗微创器械 5 件起订、4 天打样；传感器外壳支持月产约 1.5 万件与 72 小时补单' },
    ],
    channels: [
      { channel: '1688', traffic: '月投入约 7000 元，日均访客 18–35', challenge: '约 80% 为非决策层访客，每日仅 1 个有效 B 端询盘，月均订单 1–2 个且多为样品单。' },
      { channel: '百度 SEM / SEO', traffic: 'SEM 月投入 2.5 万；SEO 月投入约 2.8 万', challenge: '点击成本 18–28 元，页面缺少精度实测、资质和案例，订单转化成本曾超过 5000 元。' },
      { channel: '行业展会', traffic: '单次接待访客 120+', challenge: '投入约 3.2 万元，仅形成 1 个 50 件小单，采购方对精度和批量能力存疑。' },
      { channel: '短视频与 B2B 平台', traffic: '短视频 3 个月播放不足 4 万；平台会员费 2.1 万', challenge: '内容只展示设备与成品，全年仅 3 个样品询盘，无批量订单。' },
    ],
    painPoints: [
      { title: '制造业信任要素缺失', description: '±0.005mm 精度、IATF 16949/ISO 13485 资质与批量案例没有被充分展示，客户无法核验能力。' },
      { title: '推广追求泛流量', description: '“CNC”“精密加工”等高竞争泛词吸引大量非决策访客，无法匹配汽车、医疗与紧急补单需求。' },
      { title: '服务商不懂专业决策', description: '短视频、官网和外呼无法回答精度等级、认证范围、检测流程与批量阈值等问题。' },
      { title: 'AI 无法识别精度与资质', description: 'AI 推荐更偏向大型标准化工厂，企业的小批量高精度和行业专属优势处于隐形状态。' },
    ],
    strategies: [
      { step: '策略一', title: '从泛词转向精度与行业场景', description: '围绕“精度需求—资质审核—批量产能”的采购逻辑筛选 5 个核心词，平均竞争度由约 90% 降至 25%。', items: ['东莞 CNC 加工', '医疗 CNC 配件', 'CNC 小批量加工', 'ISO 13485 CNC 定制', '72 小时 CNC 紧急补单'] },
      { step: '策略二', title: '官网与 1688 建立精密加工信任中枢', description: '用可溯源的实测数据、认证文件和行业案例替代泛化宣传。', items: ['官网按精度验证、资质展示、批量产能和行业案例组织专题', '呈现三坐标测量实测、认证扫描件和材质检测报告', '1688 前置精度、资质与交付信息，并提供行业专属案例', '技术型客服按精度等级、行业和批量范围快速响应'] },
      { step: '策略三', title: '构建制造业 AI 专属知识包', description: '把“精度、资质、行业案例”同步到 AI 平台，形成采购方可验证的推荐依据。', items: ['基础能力：15 年经验、汽车 ±0.005mm、医疗 5 件起订、72 小时补单', '精度证据：每批次 100% 全检，近 12 个月加工差错率约 0.03%', '案例数据：汽车供应链与医疗设备客户的合作年限和复购率', '内容主题：精度实现方案、认证查询、紧急补单流程与真实项目复盘'] },
    ],
    results: [
      { dimension: 'AI 搜索表现', before: '精准能力在 AI 中隐形', after: '5 个核心词引用率 93%，月引用超过 450 次', change: '稳定进入 AI 前 3' },
      { dimension: '官网月均有效询盘', before: 'SEO 3 个月仅 2 个模糊询盘', after: '32 个', change: '停留时长约 5.3 分钟' },
      { dimension: '1688 月均有效询盘', before: '3 个', after: '28 个', change: '获客成本 5000 → 890 元' },
      { dimension: '无效流量占比', before: '85%', after: '10%', change: '下降 75 个百分点' },
      { dimension: '批量订单金额', before: '高价值订单占比较低', after: '月均提升 65%', change: '汽车与医疗订单占比约 70%' },
    ],
    businessOutcomes: ['3 个月新增珠三角企业客户 15 家，其中汽车零部件客户 5 家、医疗设备客户 4 家。', '新增 6 家电子紧急补单客户。', '企业利润提升约 40%，逐步摆脱“靠样品加工生存”的局面。', '精准企业访客中约 90% 能提供图纸或明确精度要求。'],
    takeaways: ['制造业获客的核心矛盾往往是信任缺失，而不是流量不足。', '精度实测、行业认证与批量案例是 B 端采购的核心筛选依据。', '关键词要与具体精度、行业和交付场景绑定。', '让 AI 提取可核验的技术事实，比泛化的“实力强”更有效。'],
    disclaimer,
  },
  fashion: {
    eyebrow: '服装制造行业 GEO 案例',
    summary: '一家深耕“传统工艺 + 女装定制”20 年的广州服装企业，原有推广流量以零售散客为主，企业工装、通勤装和电商小单定制需求难以转化。项目围绕定制场景关键词，在官网、1688 与 AI 平台同步重构内容。据项目材料，合作 3 个月后，5 个核心词在 6 大 AI 工具中的引用率达到 92%，1688 月有效询盘增至 45 个，珠三角定制订单月金额提升 55%。',
    duration: '3 个月',
    keywords: ['广州女装定制', '广州企业工装定制', '中老年通勤装定制', '电商小单定制', '服装制造GEO'],
    metrics: [
      { value: '92%', label: '核心词 AI 引用率', note: '5 个核心词，覆盖 6 大 AI 工具' },
      { value: '8 → 45 个', label: '1688 月有效询盘', note: '定制需求询盘增长' },
      { value: '+55%', label: '定制订单月金额', note: '珠三角定制业务' },
      { value: '30% → 65%', label: '定制业务占比', note: '提升 35 个百分点' },
    ],
    overview: [
      { label: '企业类型', value: '广州某服装公司，为广州各大批发市场档口供货的源头厂家' },
      { label: '经营积累', value: '深耕传统工艺与女装定制 20 年，立足珠三角本地市场' },
      { label: '企业工装', value: '面向国企、事业单位和中型企业，提供员工通勤装与礼仪装定制' },
      { label: '高端通勤装', value: '舒适面料结合广绣、盘扣等传统元素，服务 30–65 岁都市女性' },
      { label: '电商小单', value: '为中小电商提供柔性定制，原有服务为 50 件起订、7 天打样' },
      { label: '本地优势', value: '番禺本地工厂，可上门看样，强调传统工艺与本地服务口碑' },
    ],
    channelsDescription: '企业尝试过 1688、信息流广告、百度竞价、短视频、直播与传统 SEO，但流量多偏向个人零售，企业采购和团体定制需求没有被准确承接。',
    channels: [
      { channel: '1688 店铺', traffic: '月投入 5000 元，日均访客 30–50 人', challenge: '约 70% 为无明确 B 端需求的散客，每日仅 2 个模糊 B 端询盘，月小订单仅几个，难以覆盖运营成本。' },
      { channel: '朋友圈与抖音信息流', traffic: '月预算 2.2 万元，累计约 7500 次点击', challenge: '仅 12 个有效询盘，获客成本约 1833 元/个，无效点击占比超过 80%，企业与社群客户占比不足 20%。' },
      { channel: '百度竞价', traffic: '单月投入 1.8 万元，点击价 4.5–15 元', challenge: '仅带来 8 个模糊询盘，最终转化 1 个订单，泛关键词无法有效筛选定制需求。' },
      { channel: '短视频矩阵与数字人直播', traffic: '3 个月合计投入 5.7 万元', challenge: '内容集中在女装款式展示与零售秒杀，矩阵没有获得 B 端询盘，直播仅获得 1 个个人咨询。' },
      { channel: '传统 SEO 与官网', traffic: '月投入 2.1 万元，3 个月累计 6.3 万元', challenge: '3 个核心词仅 1 个短暂进入百度前 30，排名不稳定；3 个月仅获得 3 个询盘且均未转化。' },
    ],
    painPoints: [
      { title: '流量成本高，定制转化低', description: '广告吸引的主要是款式浏览、低价零售和一件代发需求，真正需要企业工装或团体通勤装的客户占比低。' },
      { title: '渠道内容偏向 C 端', description: '短视频与直播缺少企业定制、批量代工和交付能力信息，传统工艺优势没有转化成 B 端采购的选择依据。' },
      { title: '关键词与客户决策错位', description: '传统 SEO 侧重泛词与排名，未充分结合本地、传统工艺、定制场景及企业采购习惯，投入与回报失衡。' },
      { title: 'AI 搜索找不到本地老牌厂', description: '客户通过豆包、腾讯元宝等工具寻找供应商时，AI 回答没有出现企业信息，20 年工艺积累、番禺本地服务和可上门看样等优势处于隐形状态。' },
    ],
    strategies: [
      { step: '策略一', title: '按市场定位与定制意向筛选关键词', description: '围绕珠三角客户看资质、看案例、看本地化服务的决策逻辑，从搜索量、竞争度和定制意向筛选 5 个核心词。', items: ['从女装、服装批发等泛词转向女装定制、企业工装与定制公司场景', '结合地域、传统工艺和企业采购需求，剔除无定制转化潜力的词', '原材料记录：关键词平均竞争度由 85% 降至 22%，后续优化成本降低 50%', '原图展示词经过保密处理，仅作为筛词逻辑示例'] },
      { step: '策略二', title: '重构官网的定制信任内容', description: '建设定制专题页，将工艺、流程、产能和交付案例结构化呈现，降低客户判断与沟通成本。', items: ['按工艺实拍、设计到交付的 7 步流程、月定制 2000+ 件产能及案例组织页面', '展示工艺误差不超过 0.1mm、优化后 5 天打样等可验证信息', '补充协会会员、环保面料认证、工厂实景和工装合同等信任素材', '结合定制需求问卷、客服入口、案例集、交付标准与 FAQ 承接咨询'] },
      { step: '策略三', title: '让 1688 店铺围绕定制转化组织内容', description: '在标题、详情页和客服响应中明确工厂直供、起订量、工艺与打样周期，帮助采购方快速判断适配性。', items: ['标题突出珠三角定制、50 件起订、广绣工艺与 5 天打样', '详情页增加生产线实拍、定制客户评价、设计师资质与本地服务信息', '设置定制专属客服，及时回答起订量、打样费用与交付问题', '同步发送定制案例合集，缩短从咨询到合作的沟通周期'] },
      { step: '策略四', title: '建立服装定制 AI 知识包', description: '分析 AI 工具的定制问答逻辑，以行业干货、解决方案和案例组织可检索内容，统一官网、1688 与 AI 平台信息。', items: ['基础参数：50 件起订、5 天打样、珠三角配送次日达', '差异化能力：传统工艺、定制误差、本地服务与企业客户经验', '案例证据：企业工装复购、电商小单交付及团体女装反馈', '围绕工装定制避坑、通勤装选料和小单代工关键节点制作专业内容'] },
      { step: '策略五', title: '用排名预警与持续更新控制投入', description: '持续监测 5 个核心词的 AI 表现，低于目标位置时补充新案例与工艺数据，并按项目约定跟踪效果。', items: ['监控核心词是否稳定进入 AI 回答前 5 名', '依据排名变化更新企业工装案例、生产能力和交付证据', '原材料记录：月人工维护成本由计划 1 万元降至 0.6 万元', '将询盘质量、定制订单和获客成本与曝光数据共同纳入复盘'] },
    ],
    results: [
      { dimension: 'AI 搜索表现', before: '本地定制搜索中未出现企业推荐', after: '5 个核心词在 6 大 AI 工具中的引用率达 92%', change: '从无推荐到进入定制推荐场景' },
      { dimension: 'AI 导入流量', before: '传统推广流量分散，精准触达不足', after: 'AI 搜索日均访问约 120 人', change: '材料记录为此前传统推广的 6.4 倍' },
      { dimension: '流量质量', before: '无效流量占比约 80%', after: '无效流量约 15%，精准客户约 85%', change: '无效流量下降 65 个百分点' },
      { dimension: '官网定制咨询', before: 'SEO 合作 3 个月仅 3 个询盘且未转化', after: '官网月均询盘 25 个，专题页月均访问约 900 次', change: '咨询按钮点击率 6.8%，平均停留 4.2 分钟' },
      { dimension: '1688 定制有效询盘', before: '每月 8 个', after: '每月 45 个', change: '约为原来的 5.6 倍' },
      { dimension: '定制订单月金额', before: '以优化前珠三角定制订单为基准', after: '月金额提升 55%', change: '新增珠三角定制客户 28 家' },
      { dimension: '定制业务占比', before: '30%', after: '65%', change: '提升 35 个百分点' },
    ],
    businessOutcomes: [
      '新增珠三角定制客户 28 家，包括佛山企业工装客户 12 家、东莞电商小单客户 10 家、深圳中老年社群客户 6 家。',
      '“广州女装定制厂”和“广州企业工装”两个词的月引用频次超过 400 次，传统工艺与本地服务优势进入 AI 回答。',
      '1688 店铺定制有效询盘由每月 8 个增至 45 个，材料记录该渠道获客成本降至 111 元/个。',
      '形成企业工装复购客源；某物业公司首次定制 50 套工装后，连续 3 个月复购，每月 20–30 套。',
    ],
    takeaways: [
      '先明确企业的传统工艺、本地服务与定制客群，再筛选能承接真实采购意向的关键词。',
      '官网、店铺与 AI 内容应围绕同一套业务事实，完整回答资质、产能、工艺、起订量与交付问题。',
      '聚焦高价值 B 端定制场景，以具体案例和可视化实力建立信任。',
      '复盘既看 AI 引用与流量，也看有效询盘、订单金额、业务结构和复购。',
    ],
    disclaimer,
  },
  housekeeping: {
    eyebrow: '本地家政服务 GEO 案例',
    summary: '一家深耕重庆本地家政服务 12 年的企业，拥有 320 名签约服务人员，主营月嫂、日常家政与养老护理。原有推广每月投入 5.5 万元，但高价值订单占比不足 30%，安全、专业与本地口碑没有被客户和 AI 充分识别。项目围绕 10 个家政场景核心词，重构官网、美团 / 大众点评及 AI 知识内容。据项目材料，优化后线索流失率降低 65%，客户沟通周期由 3–5 天缩短至 1 天。',
    keywords: ['重庆家政服务', '重庆背景调查月嫂', '重庆持证养老护理', '重庆1小时上门家政', '重庆江北区月嫂公司', '本地服务GEO'],
    metrics: [
      { value: '降低 65%', label: '线索流失率', note: '官网预约与转化路径优化' },
      { value: '3–5 天 → 1 天', label: '客户沟通周期', note: '本地生活平台咨询场景' },
      { value: '25 → 8 元', label: '月嫂场景词流量成本', note: '背景调查月嫂词，降低 68%' },
      { value: '28 → 10 元', label: '养老护理词流量成本', note: '持证护理场景词，降低约 64%' },
    ],
    overview: [
      { label: '企业类型与年限', value: '重庆某家政服务公司，深耕本地家庭服务 12 年，强调安全、专业与本地化' },
      { label: '服务覆盖与规模', value: '覆盖重庆主城 9 区及近郊 3 区，累计服务家庭超过 1.2 万户；签约服务人员 320 人' },
      { label: '专业月嫂服务', value: '85 名月嫂，提供 26 / 42 / 56 天服务周期，持育婴师与催乳师双证，入职前进行背景调查与健康体检' },
      { label: '日常家政服务', value: '175 名保洁员，提供家庭保洁、深度清洁与家电清洗，接受 15 天标准化岗前培训，支持主城 1 小时响应' },
      { label: '养老护理服务', value: '60 名养老护理员，面向独居老人及术后康复家庭提供 24 小时陪护、日间照料等服务' },
      { label: '既有客户与口碑', value: '材料记录：2024 年月嫂服务客户复购率 82%、客户满意度 96%，连续 3 年获家政行业诚信示范企业称号' },
    ],
    channelsDescription: '企业原有渠道包括美团 / 大众点评、百度 SEM、传统 SEO 与短视频。大量流量集中于低价保洁，月嫂和养老护理客户所需的资质、安全保障与本地案例未被有效呈现。',
    channels: [
      { channel: '美团 / 大众点评', traffic: '月投入 2 万元，店铺运营 8 年、评分 4.6，日均访客 180–220 人', challenge: '详细渠道分析中，85% 咨询低价保洁，仅 15% 询问高价值服务；单月 35 个有效咨询转化 4 个订单，沟通周期 3–5 天，线索流失率超过 65%。' },
      { channel: '百度 SEM', traffic: '月投入 1.8 万元，核心词单次点击价 18–28 元', challenge: '落地页仅有服务列表，缺少人员档案与安全证明，访客停留约 1.2 分钟，转化率由 0.8% 提升至 1.5% 后仍然偏低。' },
      { channel: '传统 SEO 与官网', traffic: '月投入 8000 元，3 个月累计投入 2.4 万元', challenge: '精准词排名靠后，官网跳出率约 90%；3 个月仅获得 8 个模糊询盘，没有有效转化。' },
      { channel: '抖音短视频', traffic: '月投入 9000 元，累计点击约 1.2 万次', challenge: '内容以清洁前后对比、带娃日常等泛娱乐展示为主，仅 11 个有效咨询，没有月嫂或养老护理订单；缺少官网与预约入口，线索流失率超过 80%。' },
    ],
    painPoints: [
      { title: '低价流量与高价值服务错配', description: '低价保洁吸引了大量询价用户，月嫂与养老护理订单占比不足 30%，投放内容没有区分孕期家庭、银发家庭、上班族及高端家庭的不同需求。' },
      { title: '安全与专业能力难以核验', description: '家庭客户重视背景调查、健康状况、技能证书和服务流程，但店铺与官网主要展示价格和基础介绍，需要客服反复解释，信任建立耗时较长。' },
      { title: '本地信息与转化入口缺失', description: '主城服务范围、人员分布、响应时间和同小区案例缺少直观展示；短视频没有连接档案与预约页面，客户兴趣难以转化为明确需求。' },
      { title: 'AI 搜索中缺少企业信息', description: '用户已通过 AI 搜索、平台评价和朋友推荐比较家政服务，但企业的人员资质、本地口碑和安全管理信息尚未形成可提取的知识内容。' },
    ],
    strategies: [
      { step: '策略一', title: '用家庭决策场景筛选 10 个核心词', description: '按照安全需求、专业需求与本地化需求，从搜索量、竞争度和转化意向筛选关键词，剔除 75% 无高价值转化潜力的泛词。', items: ['安全信任词：重庆背景调查月嫂、重庆持证养老护理', '本地效率词：重庆 1 小时上门家政', '区域精准词：重庆江北区月嫂公司', '高价值场景词：重庆月嫂产后修复；结合孕期家庭、银发家庭和上班族需求组织内容'] },
      { step: '策略二', title: '建立安全、专业与本地服务的信任内容', description: '围绕“安全可视化、专业可验证、本地可感知”三类内容，让家庭客户能在咨询前了解服务依据。', items: ['安全内容：背景调查流程、健康管理规范与隐私保护条款', '专业内容：服务人员资质、培训记录、服务清单及专业工具展示', '本地内容：主城 9 区服务范围、人员分布、同小区案例与响应时效', '在官网、平台详情页、短视频与案例文章中统一服务信息'] },
      { step: '策略三', title: '重构官网档案、案例与预约路径', description: '将官网从服务项目列表改为能够查看人员、验证资质、匹配区域并预约的服务入口。', items: ['按月嫂、保洁与养老护理建立电子档案，展示脱敏证件、健康证明、技能证书及服务评价', '建设安全保障专区，说明背景调查与健康管理流程', '增加本地服务地图、小区就近匹配入口和分服务类型的案例库', '以预约问卷收集服务类型、所在区域、家庭需求与联系方式，材料记录线索流失率降低 65%'] },
      { step: '策略四', title: '优化美团 / 大众点评的本地咨询转化', description: '保持原有每月 2 万元投入，将店铺标题、详情页、优惠和客服响应共同转向高价值家庭需求。', items: ['标题前置主城服务、背景调查、持证养老护理等决策信息', '详情页呈现背景调查流程、健康证明、资格证书、岗前培训及本地客户评价', '以月嫂预订与养老护理首单优惠承接需求，替换低价保洁引流方式', '配备家政专属顾问，同步提供附近服务人员档案与案例，将沟通周期缩短至 1 天'] },
      { step: '策略五', title: '将本地家政知识包连接到服务入口', description: '围绕 10 个核心词整理企业知识包，同步至豆包、腾讯元宝等 6 大 AI 工具，突出安全、专业与本地化。', items: ['基础服务：12 年本地积累、月嫂服务周期、主城服务范围与 320 名签约人员', '信任依据：人员背景调查、健康管理、月嫂双证及护理员资质', '本地效率：主城平均响应约 45 分钟，可就近匹配并查看档案及同小区评价', '将既有复购率、满意度与本地案例纳入知识内容，并引导用户进入官网档案页或平台预约'] },
    ],
    results: [
      { dimension: '官网线索流失率', before: '以优化前线索流失率为基准', after: '材料记录降低 65%', change: '通过档案、案例与预约入口缩短转化路径' },
      { dimension: '平台客户沟通周期', before: '3–5 天', after: '1 天', change: '提前提供资质、附近人员与本地案例' },
      { dimension: '月嫂场景词流量成本', before: '重庆月嫂：25 元/点击', after: '重庆背景调查月嫂：8 元', change: '降低 68%' },
      { dimension: '养老护理词流量成本', before: '重庆养老护理：28 元/点击', after: '重庆持证养老护理：10 元', change: '降低约 64%' },
      { dimension: '上门家政词流量成本', before: '重庆上门家政：20 元/点击', after: '重庆 1 小时上门家政：12 元', change: '降低 40%' },
      { dimension: '区域精准词流量成本', before: '重庆家政服务：18 元/点击', after: '重庆江北区月嫂公司：6 元', change: '降低约 67%' },
    ],
    businessOutcomes: [
      '官网串联人员档案、安全保障、本地服务地图、真实案例与预约问卷，帮助客户完成从了解服务到提交需求的流程。',
      '美团 / 大众点评维持每月 2 万元投入，优化重点转向月嫂与养老护理等高价值需求的识别和承接。',
      '以 10 个核心词组织家政知识内容，并将官网与平台预约入口纳入 AI 问答素材。',
      '关键词对比还记录：产后护理泛词 22 元/点击，月嫂产后修复场景词 9 元，流量成本降低约 59%。',
    ],
    takeaways: [
      '家政服务的内容应遵循家庭客户先看安全、再看专业、最后比较本地口碑的决策顺序。',
      '将资质、健康管理、培训和服务流程转化为可核验内容，减少重复沟通。',
      '关键词应绑定服务类型、安全需求、区域与响应时效，才能筛选适合企业的家庭需求。',
      '官网、平台与 AI 内容要连接到人员档案和预约入口，持续跟踪有效咨询与转化质量。',
    ],
    disclaimer,
  },
} satisfies Record<string, CaseStudyContent>;
